import { popoverAnatomy as parts } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers, defineStyle } from '@chakra-ui/styled-system';
import { cssVar } from '@chakra-ui/theme-tools';
import { Space, Colors, TextStyles, BorderWidths, Radii, FontSizes, Shadows } from '../../types';

const { defineMultiStyleConfig, definePartsStyle } = createMultiStyleConfigHelpers(parts.keys);

const $popperBg = cssVar('popper-bg');
const $popperBorderColor = cssVar('popper-border');
const $arrowSize = cssVar('popper-arrow-size');
const $arrowBg = cssVar('popper-arrow-bg');
const $arrowShadowColor = cssVar('popper-arrow-shadow-color');
const $closeButtonSize = cssVar('close-button-size');
const baseStylePopper = defineStyle({ zIndex: 'tooltip' });

const baseStyleContent = defineStyle({
  [$popperBg.variable]: `colors.${Colors.SURFACE_ELEVATED}`,
  [$popperBorderColor.variable]: `colors.${Colors.BORDER_DEFAULT}`,
  [$closeButtonSize.variable]: `space.${Space.SPACE_6}`,
  [$arrowSize.variable]: `space.${Space.SPACE_3}`,
  [$arrowBg.variable]: $popperBg.reference,
  [$arrowShadowColor.variable]: $popperBorderColor.reference,
  border: 'none',
  bg: $popperBg.reference,
  maxW: 'xs',
  borderRadius: Radii.XL,
  boxShadow: Shadows.LG,
  zIndex: 'inherit',
  _focusVisible: {
    outline: 0,
    boxShadow: Shadows.LG,
  },
  _dark: {
    border: '1px solid var(--popper-border)',
    [$popperBorderColor.variable]: `colors.${Colors.SURFACE_DIALOG}`,
    [$arrowShadowColor.variable]: $popperBorderColor.reference,
    [$popperBg.variable]: `colors.${Colors.SURFACE_ELEVATED}`,
    [$arrowBg.variable]: $popperBg.reference,
  },
});

const baseStyleHeader = defineStyle({
  px: Space.SM,
  pt: Space.SM,
  pb: Space.XS,
  borderBottomWidth: BorderWidths.XS,
  borderBottomColor: Colors.BORDER_ELEVATED,
  apply: `textStyles.${TextStyles.H4}`,
  color: Colors.TEXT_EMPHASIS,
});

const baseStyleBody = defineStyle({
  p: Space.SM,
  apply: `textStyles.${TextStyles.P2}`,
  color: Colors.TEXT_MUTED,
});

const baseStyleFooter = defineStyle({
  px: Space.SM,
  py: Space.XS,
  borderTopColor: Colors.BORDER_ELEVATED,
  borderBottomRadius: Radii.LG,
});

const baseStyleCloseButton = defineStyle({
  position: 'absolute',
  insetBlockStart: Space.XS,
  insetInlineEnd: Space.XS,
  borderRadius: Radii.SM,
  padding: Space.SPACE_0_5,
  fontSize: FontSizes['2XS'],
  h: $closeButtonSize.reference,
  w: $closeButtonSize.reference,
  color: Colors.ICON_DEFAULT,
  _hover: {
    color: Colors.ICON_HOVER,
    bg: Colors.SURFACE_TERTIARY_HOVER,
  },
  _active: {
    color: Colors.ICON_ACTIVE,
    bg: Colors.SURFACE_TERTIARY_ACTIVE,
  },
});

const baseStyle = definePartsStyle({
  popper: baseStylePopper,
  content: baseStyleContent,
  header: baseStyleHeader,
  body: baseStyleBody,
  footer: baseStyleFooter,
  closeButton: baseStyleCloseButton,
});

export const Popover = defineMultiStyleConfig({
  baseStyle,
});
